import { forwardRef, useRef, useState, ReactEventHandler, ForwardedRef, useEffect, useCallback } from 'react';
import { motion, useAnimation } from 'framer-motion';
import dynamic from 'next/dynamic';
import { Box, BoxProps, Flex } from '@mezzoforte/forge';
import type CSS from 'csstype';
import { CarouselState } from '@/components/ImageCarousel/useCarousel';
import { VideoPlayerProps } from '@/components/ImageCarousel/VideoSlide';
import { CarouselItem } from '@/components/ImageCarousel/CarouselItem';
import { Picture } from '@/components/Image/Picture';

const VideoSlide = dynamic(() => import('./VideoSlide'), { ssr: false });

interface SlideProps {
  item: CarouselItem;
  priority?: boolean;
  isFirstItem?: boolean;
  onVideoPlay?: VideoPlayerProps['onPlay'];
}

const Slide = forwardRef<HTMLDivElement, SlideProps>(
  ({ item, priority, isFirstItem, onVideoPlay }: SlideProps, ref: ForwardedRef<HTMLDivElement>) => {
    const [objectFit, setObjectFit] = useState<CSS.Properties['objectFit']>('contain');
    const imageRef = useRef<HTMLImageElement>(null);

    const handleFirstImageObjectFit = useCallback(
      (img: HTMLImageElement) => {
        const { naturalWidth, naturalHeight } = img;

        // If the first image is letterboxed or slightly slimmer than 4/3 image ratio, set object-fit to cover
        if (isFirstItem && naturalWidth / naturalHeight - 4 / 3 > -0.12) {
          setObjectFit('cover');
        }
      },
      [isFirstItem]
    );

    const onLoad: ReactEventHandler<HTMLImageElement> = useCallback(
      (e) => handleFirstImageObjectFit(e.currentTarget),
      [handleFirstImageObjectFit]
    );

    useEffect(() => {
      if (!imageRef.current) {
        return;
      }

      if (imageRef.current.complete) {
        // Image is already loaded, onLoad will never trigger.
        handleFirstImageObjectFit(imageRef.current);
      }
    }, [handleFirstImageObjectFit]);

    if (!item) return null;

    return (
      <Flex
        ref={ref}
        position="relative"
        flex="1 0 100%"
        backgroundColor="backgroundGray"
        borderRadius={{ base: 0, md: '6px' }}
        justifyContent="center"
        pointerEvents={item.videoId ? 'initial' : 'none'}
      >
        {item.videoId === null ? (
          <Box
            position="relative"
            width="100%"
          >
            <Picture
              ref={imageRef}
              formats={item.main}
              fetchPriority={priority ? 'high' : 'auto'}
              style={{ visibility: 'initial', objectFit }}
              onLoad={onLoad}
            />
          </Box>
        ) : (
          <VideoSlide
            videoId={item.videoId}
            onPlay={onVideoPlay}
          />
        )}
      </Flex>
    );
  }
);

export interface CarouselProps extends BoxProps {
  state: CarouselState<CarouselItem>;
  increment: () => void;
  decrement: () => void;
  opts?: {
    stiffness: number;
    damping: number;
    swipeDetect: number;
    dragElastic: number;
  };
  onVideoPlay?: VideoPlayerProps['onPlay'];
}

function swipePower(offset: number, distance: number) {
  if (distance === 0) return 0;
  return offset / distance;
}

const defaultAnimationOptions = {
  dragElastic: 1,
  stiffness: 600,
  damping: 50,
  swipeDetect: 0.2,
};

export function CarouselTrack({
  state,
  increment,
  decrement,
  opts = defaultAnimationOptions,
  onVideoPlay,
  ...props
}: CarouselProps) {
  const el = useRef<HTMLDivElement>(null);
  const inMotion = useRef(false);
  const animation = useAnimation();
  const { dragElastic, stiffness, damping, swipeDetect } = opts;

  return (
    <Box
      height="100%"
      width="100%"
      position="absolute"
      top={0}
      left={0}
      {...props}
      onClick={(e) => {
        if ('tagName' in e.target && e.target.tagName === 'A') return;
        if (state.visibleItems.at(1)?.videoId) return;
        if (inMotion.current) {
          e.preventDefault();
          return;
        }
        props.onClick?.(e);
      }}
    >
      <motion.div
        key={state.currentIndex}
        animate={animation}
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: 0, right: 0 }}
        transition={{ x: { type: 'spring', stiffness, damping } }}
        variants={{
          toLeft: { x: '-100%' },
          toRight: { x: '100%' },
          center: { x: 0 },
        }}
        onDragStart={() => {
          inMotion.current = true;
        }}
        dragElastic={dragElastic}
        onDragEnd={async (_, { offset }) => {
          const power = swipePower(offset.x, el.current?.getBoundingClientRect().width ?? 0);
          if (power > swipeDetect) {
            await animation.start('toRight');
            decrement();
          } else if (power < -swipeDetect) {
            await animation.start('toLeft');
            increment();
          }
          inMotion.current = false;
        }}
        style={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Slide item={state.visibleItems[0]} />
        <Slide
          item={state.visibleItems[1]}
          priority
          ref={el}
          isFirstItem={state.currentIndex === 0}
          onVideoPlay={onVideoPlay}
        />
        <Slide item={state.visibleItems[2]} />
      </motion.div>
    </Box>
  );
}
